import React from 'react';
import { ProcessingStatus } from '../types/nric';

interface ProgressIndicatorProps {
  status: ProcessingStatus;
}

const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({ status }) => {
  const getStageIcon = (stage: string, isActive: boolean, isCompleted: boolean) => {
    const baseClasses = "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium";
    const activeClasses = isActive ? "bg-primary-600 text-white" : 
                         isCompleted ? "bg-green-500 text-white" : 
                         "bg-gray-200 text-gray-500";
    
    return (
      <div className={`${baseClasses} ${activeClasses}`}>
        {isCompleted ? (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        ) : isActive ? (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
        ) : (
          <span>{getStageNumber(stage)}</span>
        )}
      </div>
    );
  };

  const getStageNumber = (stage: string): number => {
    switch (stage) {
      case 'loading': return 1;
      case 'recognizing': return 2;
      case 'parsing': return 3;
      case 'complete': return 4;
      default: return 1;
    }
  };

  const stages = [
    { key: 'loading', label: 'Loading Image', description: 'Preparing image for processing' },
    { key: 'recognizing', label: 'OCR Recognition', description: 'Extracting text from image' },
    { key: 'parsing', label: 'Data Parsing', description: 'Identifying NRIC information' },
    { key: 'complete', label: 'Complete', description: 'Processing finished' }
  ];

  if (status.stage === 'idle') {
    return null;
  }

  if (status.stage === 'error') {
    return (
      <div className="card border-red-200 bg-red-50">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <p className="font-medium text-red-800">Processing Error</p>
            <p className="text-sm text-red-600">{status.message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Processing NRIC Image</h3>
          <span className="text-sm text-gray-500">{Math.round(status.progress)}%</span>
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-primary-600 h-2 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${status.progress}%` }}
          ></div>
        </div>

        <div className="space-y-4">
          {stages.map((stage, index) => {
            const isActive = status.stage === stage.key;
            const isCompleted = getStageNumber(status.stage) > getStageNumber(stage.key);
            
            return (
              <div key={stage.key} className="flex items-center space-x-4">
                {getStageIcon(stage.key, isActive, isCompleted)}
                <div className="flex-1">
                  <p className={`font-medium ${isActive ? 'text-primary-600' : isCompleted ? 'text-green-600' : 'text-gray-500'}`}>
                    {stage.label}
                  </p>
                  <p className="text-sm text-gray-500">{stage.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        {status.message && (
          <div className="mt-4 p-3 bg-blue-50 rounded-md">
            <p className="text-sm text-blue-700">{status.message}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgressIndicator;
